/**
 * @file Components/Sections/Package1.jsx
 * @description Tour packages carousel section. Fetches tours marked showOnHomepage
 * from the API and displays them in a Swiper carousel with proper links.
 */

import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { FaMapMarkerAlt, FaClock, FaStar, FaArrowLeft, FaArrowRight, FaCheck, FaEye } from 'react-icons/fa';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { toast } from 'react-toastify';
import api from '../../services/api';

const TourPackages = () => {
  const navigate = useNavigate();
  const swiperRef = useRef(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHomepageTours = async () => {
      try {
        const { data } = await api.get('/tours', { params: { showOnHomepage: 'true', limit: 10 } });
        setTours(data?.data?.tours || []);
      } catch (err) {
        console.error('Error fetching homepage tours:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchHomepageTours();
  }, []);

  const handleBookNow = (tourId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.info('Please login to book a tour');
      navigate('/login', { state: { from: `/tours/${tourId}/book` } });
      return;
    }
    navigate(`/tours/${tourId}/book`);
  };

  const handleSlideChange = () => {
    if (swiperRef.current) {
      setIsBeginning(swiperRef.current.swiper.isBeginning);
      setIsEnd(swiperRef.current.swiper.isEnd);
    }
  };

  if (loading) {
    return (
      <section className="py-16 bg-gradient-to-br from-white via-blue-50 to-indigo-50">
        <div className="container mx-auto px-4 text-center">
          <div className="h-8 w-72 bg-gray-200 rounded animate-pulse mx-auto mb-8" />
          <div className="flex gap-8 overflow-hidden justify-center">
            {[1, 2, 3].map(i => (
              <div key={i} className="min-w-[340px] h-[420px] bg-gray-200 rounded-3xl animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (tours.length === 0) return null;

  return (
    <section className="py-16 bg-gradient-to-br from-white via-blue-50 to-indigo-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4 relative inline-block">
            Popular Tour Packages
            <div className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-[#5365dc] to-[#243185]"></div>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto mt-6 text-lg">
            Discover our handpicked selection of premium travel experiences across India
          </p>
        </div>

        <div className="relative max-w-7xl mx-auto">
          <button
            onClick={() => swiperRef.current?.swiper.slidePrev()}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm p-4 rounded-full shadow-lg transform -translate-x-1/2 transition-all ${
              isBeginning ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110 hover:bg-white'
            }`}
            disabled={isBeginning}
          >
            <FaArrowLeft className="text-[#5365dc] text-2xl" />
          </button>

          <button
            onClick={() => swiperRef.current?.swiper.slideNext()}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm p-4 rounded-full shadow-lg transform translate-x-1/2 transition-all ${
              isEnd ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110 hover:bg-white'
            }`}
            disabled={isEnd}
          >
            <FaArrowRight className="text-[#5365dc] text-2xl" />
          </button>

          <Swiper
            ref={swiperRef}
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={40}
            slidesPerView={1}
            centeredSlides={true}
            loop={tours.length > 3}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            pagination={{
              clickable: true,
              dynamicBullets: true,
            }}
            onSlideChange={handleSlideChange}
            breakpoints={{
              640: { slidesPerView: 1.5 },
              768: { slidesPerView: 2.2 },
              1024: { slidesPerView: 3 },
            }}
            className="py-12 px-4"
          >
            {tours.map((tour) => (
              <SwiperSlide key={tour._id}>
                <div className="group bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transform transition-all duration-300 hover:-translate-y-2">
                  <div className="relative h-56 md:h-72">
                    <img
                      src={tour.thumbnail || tour.images?.[0] || 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=600&q=80'}
                      alt={tour.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent">
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <h3 className="text-2xl font-bold mb-2">{tour.title}</h3>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center">
                            <FaMapMarkerAlt className="text-[#5365dc] mr-2" />
                            <span className="text-white/90">{tour.location}</span>
                          </div>
                          <div className="flex items-center">
                            <FaClock className="text-[#5365dc] mr-2" />
                            <span className="text-white/90">{tour.duration} Days</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg">
                      <span className="text-[#5365dc] font-bold text-lg">₹{tour.price?.toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="p-6">
                    <p className="text-gray-600 mb-4 line-clamp-2">{tour.description}</p>

                    {tour.highlights && tour.highlights.length > 0 && (
                      <div className="space-y-2 mb-6">
                        {tour.highlights.slice(0, 3).map((highlight, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <FaCheck className="text-[#5365dc] flex-shrink-0" />
                            <span className="text-gray-700 text-sm">{highlight}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center">
                        <div className="flex text-yellow-400">
                          {[...Array(5)].map((_, i) => (
                            <FaStar
                              key={i}
                              className={i < Math.floor(tour.rating || 0) ? 'text-yellow-400' : 'text-gray-300'}
                            />
                          ))}
                        </div>
                        <span className="ml-2 text-gray-600 text-sm">
                          {tour.rating?.toFixed(1)}
                        </span>
                      </div>
                      {tour.pricing?.group && tour.pricing.group < tour.price && (
                        <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                          Group from ${tour.pricing.group}
                        </span>
                      )}
                    </div>

                    <div className="flex space-x-4">
                      <Link
                        to={`/tours/${tour._id}`}
                        className="flex-1 flex items-center justify-center space-x-2 bg-gray-100 text-gray-800 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-300"
                      >
                        <FaEye />
                        <span>View Details</span>
                      </Link>
                      <button
                        onClick={() => handleBookNow(tour._id)}
                        className="flex-1 bg-gradient-to-r from-[#5365dc] to-[#243185] text-white py-3 rounded-xl font-semibold hover:shadow-lg hover:from-[#243185] hover:to-[#5365dc] transition-all duration-300"
                      >
                        Book Now
                      </button>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
};

export default TourPackages;

